import { ExternalLink, RefreshCcw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  formatPrice,
  formatShortDate,
  getHighestSnapshot,
  getLowestSnapshot,
  getPreviousSnapshot,
  getPriceDelta,
} from "@/lib/price";
import type { Product } from "@/mocks/products";
import { MetricCard } from "@/components/metric-card";
import { StatusBadge } from "@/components/status-badge";

type ProductOverviewProps = {
  product: Product;
  onMockRecrawl: () => void;
};

export function ProductOverview({
  product,
  onMockRecrawl,
}: ProductOverviewProps): JSX.Element {
  const lowest = getLowestSnapshot(product.snapshots);
  const highest = getHighestSnapshot(product.snapshots);
  const previous = getPreviousSnapshot(product.snapshots);
  const delta = getPriceDelta(product);
  const bestListing = product.listings
    .filter((listing) => listing.price !== null)
    .sort((left, right) => (left.price ?? Infinity) - (right.price ?? Infinity))[0];
  const atCollectedLow =
    lowest !== undefined &&
    product.latestPrice !== null &&
    product.latestPrice <= lowest.price;

  return (
    <div className="grid gap-4">
      <section className="grid min-h-[188px] overflow-hidden rounded-lg border border-[#20304a] bg-[#071120] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] md:grid-cols-[248px_minmax(0,1fr)]">
        <div className="relative grid min-h-[188px] place-items-center overflow-hidden border-b border-[#20304a] bg-[#050b15] md:border-b-0 md:border-r">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_60%,rgba(255,92,20,0.55),rgba(255,92,20,0.12)_28%,rgba(4,9,17,0)_58%)]" />
          <img
            alt=""
            className="relative h-[152px] w-[152px] rounded-xl object-cover shadow-2xl"
            src={product.imageUrl}
          />
        </div>
        <Card className="min-w-0 rounded-none border-0 bg-transparent py-0 shadow-none">
          <CardContent className="grid h-full content-center gap-3 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <StatusBadge status={product.crawlStatus} />
                <span className="break-all text-sm font-medium text-[#93a0b8]">
                  {product.canonicalProductKey}
                </span>
              </div>
              <span className="inline-flex items-center gap-2 text-sm font-medium text-[#d6deeb]">
                <span className="h-2.5 w-2.5 rounded-full bg-[#35f36c] shadow-[0_0_18px_rgba(53,243,108,0.9)]" />
                Tracking
              </span>
            </div>
            <div className="grid min-w-0 gap-1">
              <h1 className="break-words text-2xl font-extrabold leading-tight text-white sm:text-[26px]">
                {product.productName}
              </h1>
              <p className="text-base font-medium text-[#9aa7bb]">
                {product.identifierLabel} · {product.listings.length} listings
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                asChild
                className="h-9 rounded-md bg-[#ff3d16] px-4 font-extrabold text-white shadow-[0_10px_28px_rgba(255,61,22,0.28)] hover:bg-[#ff4a25]"
              >
                <a href={product.canonicalUrl} rel="noreferrer" target="_blank">
                  <ExternalLink className="h-4 w-4" />
                  Shopee
                </a>
              </Button>
              <Button
                className="h-9 rounded-md border-[#23334d] bg-[#070f1c] px-4 font-bold text-white hover:bg-[#0c192b]"
                onClick={onMockRecrawl}
                type="button"
                variant="outline"
              >
                <RefreshCcw className="h-4 w-4" />
                Mock recrawl
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          detail={bestListing ? bestListing.shopName : "Pending"}
          label="Best offer"
          tone={atCollectedLow ? "good" : "default"}
          value={formatPrice(bestListing?.price)}
        />
        <MetricCard
          detail={lowest ? formatShortDate(lowest.capturedAt) : "Pending"}
          label="Lowest (30D)"
          tone="good"
          value={formatPrice(lowest?.price)}
        />
        <MetricCard
          detail={highest ? formatShortDate(highest.capturedAt) : "Pending"}
          label="Highest (30D)"
          value={formatPrice(highest?.price)}
        />
        <MetricCard
          detail={previous ? `vs ${formatShortDate(previous.capturedAt)}` : "Pending"}
          label="Change (30D)"
          tone={delta === undefined ? "default" : delta <= 0 ? "good" : "warn"}
          value={
            delta === undefined
              ? "Pending"
              : `${delta <= 0 ? "-" : "+"}${formatPrice(Math.abs(delta))}`
          }
        />
      </section>
      <section className="grid gap-3 rounded-lg border border-[#20304a] bg-[#071120] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-extrabold text-white">Merged Shopee listings</h2>
          <span className="text-sm font-medium text-[#9aa7bb]">
            {product.mergeStatus.replace("_", " ")}
          </span>
        </div>
        <div className="grid gap-2">
          {product.listings.map((listing) => {
            const isBest = bestListing?.listingKey === listing.listingKey;
            return (
              <a
                className="grid min-h-[54px] grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-md border border-[#20304a] bg-[#081323] px-3 py-2 hover:border-[#ff4a25]/70"
                href={listing.canonicalUrl}
                key={listing.listingKey}
                rel="noreferrer"
                target="_blank"
              >
                <span className="grid min-w-0 gap-1">
                  <span className="truncate text-sm font-extrabold text-white">
                    {listing.shopName}
                  </span>
                  <span className="truncate text-xs font-medium text-[#8d9aaf]">
                    {listing.listingKey}
                  </span>
                </span>
                <span className="grid justify-items-end gap-1 text-right">
                  <strong className="text-sm font-extrabold text-white">
                    {formatPrice(listing.price)}
                  </strong>
                  <span className={isBest ? "text-xs font-bold text-[#35f36c]" : "text-xs font-medium text-[#9aa7bb]"}>
                    {isBest ? "Best tracked" : formatShortDate(listing.lastSuccessAt)}
                  </span>
                </span>
              </a>
            );
          })}
        </div>
      </section>
    </div>
  );
}
